import { useCallback, useEffect, useState } from "react";
import { Loader2, Save, Sparkles } from "lucide-react";
import { authClient } from "../../lib/auth";

const CATEGORIES = [
  { slug: "soins-visage",             label: "Soins visage",   grid: "cosmetique" },
  { slug: "soins-corps",              label: "Soins corps",    grid: "cosmetique" },
  { slug: "soins-cheveux",            label: "Soins cheveux",  grid: "cosmetique" },
  { slug: "complements-alimentaires", label: "Compléments",    grid: "complement" },
  { slug: "teint",                    label: "Teint",          grid: "maquillage" },
  { slug: "yeux",                     label: "Yeux",           grid: "maquillage" },
  { slug: "levres",                   label: "Lèvres",         grid: "maquillage" },
];

export default function TrustConfigSection() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [rescoring, setRescoring] = useState(false);
  const [pillsDraft, setPillsDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authClient.get("/admin/trust/config");
      setConfig(data);
      const draft = {};
      Object.entries(data.pills || {}).forEach(([k, v]) => { draft[k] = (v || []).join(" · "); });
      setPillsDraft(draft);
    } catch {
      setConfig(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateBaseline = (slug, idx, value) => {
    const v = Math.max(0, Math.min(10, parseInt(value || "0", 10)));
    setConfig((c) => {
      const next = { ...c, baselines: { ...c.baselines } };
      const arr = [...(next.baselines[slug] || [7, 7, 7, 7, 7, 7])];
      while (arr.length < 6) arr.push(7);
      arr[idx] = v;
      next.baselines[slug] = arr;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setMessage("");
    try {
      const pills = {};
      Object.entries(pillsDraft).forEach(([k, s]) => {
        pills[k] = s.split(/[·\n,]/).map((x) => x.trim()).filter(Boolean).slice(0, 5);
      });
      await authClient.put("/admin/trust/config", {
        baselines: config.baselines,
        pills,
      });
      setMessage("Configuration enregistrée ✔");
      await load();
    } catch (e) {
      setMessage("Erreur : " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const rescore = async () => {
    setRescoring(true);
    setMessage("");
    try {
      await authClient.post("/admin/trust/baseline-full");
      setMessage("Recalcul lancé — les scores seront mis à jour dans quelques secondes.");
    } catch (e) {
      setMessage("Erreur : " + (e.response?.data?.detail || e.message));
    } finally {
      setRescoring(false);
    }
  };

  if (loading || !config) {
    return (
      <section className="mb-16 border border-bl-border p-8">
        <div className="flex items-center gap-3 text-bl-textSoft"><Loader2 size={16} className="animate-spin" /> Chargement de la configuration…</div>
      </section>
    );
  }

  return (
    <section className="mb-16 border border-bl-border p-8" data-testid="admin-trust-config">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <span className="overline">Baselines & pastilles</span>
          <h2 className="font-serif text-2xl mt-1">Configuration du badge de confiance</h2>
          <p className="text-sm text-bl-textSoft mt-2 max-w-2xl">
            Ajustez les 6 sous-scores baseline (0-10) et les pastilles de chaque catégorie.
            Puis relancez le calcul pour propager sur les {config.baselines ? Object.keys(config.baselines).length : 0} catégories.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={save}
            disabled={saving}
            className="border border-bl-mauveDeep text-bl-mauveDeep px-5 py-2.5 text-sm tracking-wide hover:bg-bl-mauveDeep hover:text-white transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="trust-config-save"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Enregistrer
          </button>
          <button
            onClick={rescore}
            disabled={rescoring}
            className="bg-bl-text text-bl-bg px-5 py-2.5 text-sm tracking-wide hover:bg-bl-dore transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="trust-config-rescore"
          >
            {rescoring ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            Recalculer les scores
          </button>
        </div>
      </div>

      {message && (
        <div className="mb-6 p-3 border border-bl-border bg-bl-soft text-sm" data-testid="trust-config-msg">
          {message}
        </div>
      )}

      <div className="space-y-8">
        {CATEGORIES.map((cat) => (
          <CategoryEditor
            key={cat.slug}
            cat={cat}
            criteria={config.criteria?.[cat.grid] || []}
            baseline={config.baselines[cat.slug] || [7, 7, 7, 7, 7, 7]}
            pillsDraft={pillsDraft[cat.slug] || ""}
            onBaselineChange={(idx, v) => updateBaseline(cat.slug, idx, v)}
            onPillsChange={(v) => setPillsDraft((d) => ({ ...d, [cat.slug]: v }))}
          />
        ))}
      </div>
    </section>
  );
}

const CategoryEditor = ({ cat, criteria, baseline, pillsDraft, onBaselineChange, onPillsChange }) => {
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const avg = (() => {
    let num = 0, den = 0;
    criteria.forEach((c, i) => { num += (baseline[i] || 0) * (c.weight || 1); den += (c.weight || 1); });
    return den ? (num / den).toFixed(1) : "—";
  })();

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const pills = pillsDraft.split(/[·\n,]/).map((x) => x.trim()).filter(Boolean).slice(0, 5);
      const { data } = await authClient.post("/admin/trust/preview", {
        category: cat.slug,
        baseline,
        pills,
        sample_size: 3,
      });
      setPreview(data);
    } finally { setPreviewing(false); }
  };

  return (
    <div className="border border-bl-border p-6 bg-white/70">
      <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
        <div>
          <span className="overline">Grille {cat.grid}</span>
          <h3 className="font-serif text-xl text-bl-text mt-1">{cat.label}</h3>
        </div>
        <div className="text-right">
          <span className="overline">Score moyen</span>
          <p className="font-serif text-3xl text-bl-fuchsia mt-1" data-testid={`avg-${cat.slug}`}>{avg}</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {criteria.map((c, i) => (
          <div key={c.key}>
            <div className="flex justify-between items-baseline mb-1">
              <label className="text-xs uppercase tracking-wide text-bl-textSoft">{c.label}</label>
              <span className="text-sm text-bl-mauveDeep font-medium" data-testid={`val-${cat.slug}-${c.key}`}>
                {baseline[i] ?? 7}/10
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              step="1"
              value={baseline[i] ?? 7}
              onChange={(e) => onBaselineChange(i, e.target.value)}
              className="w-full accent-bl-fuchsia"
              data-testid={`slider-${cat.slug}-${c.key}`}
            />
            <span className="text-[10px] text-bl-textSoft">Poids × {c.weight}</span>
          </div>
        ))}
      </div>

      <div className="mt-4">
        <label className="text-xs uppercase tracking-wide text-bl-textSoft block mb-1">
          Pastilles (jusqu&apos;à 5, séparées par · ou ,)
        </label>
        <input
          type="text"
          value={pillsDraft}
          onChange={(e) => onPillsChange(e.target.value)}
          className="w-full border border-bl-border px-3 py-2 text-sm bg-white"
          placeholder="Sans Parabène · Clean Beauty · Végan…"
          data-testid={`pills-${cat.slug}`}
        />
      </div>

      <div className="mt-4">
        <div className="flex gap-2 items-center flex-wrap">
          <button
            onClick={runPreview}
            disabled={previewing}
            className="text-xs tracking-wide text-bl-mauveDeep border border-bl-border px-3 py-1.5 hover:bg-bl-soft transition-colors disabled:opacity-50"
            data-testid={`preview-${cat.slug}`}
          >
            {previewing ? "Aperçu…" : "Aperçu sur 3 produits ✨"}
          </button>
          <RescoreCategoryButton slug={cat.slug} label={cat.label} />
        </div>
        {preview && (
          <div className="mt-3 border-l-2 border-bl-fuchsia pl-3 space-y-2 text-xs" data-testid={`preview-out-${cat.slug}`}>
            {preview.samples.length === 0 && <span className="text-bl-textSoft">{preview.warning}</span>}
            {preview.samples.map((s) => (
              <div key={s.slug} className="flex items-center gap-2">
                <span className="text-bl-textSoft truncate max-w-[180px]">{s.name}</span>
                <span className="text-bl-textSoft">·</span>
                <span className="text-red-500">{s.current?.global_score ?? "—"}</span>
                <span>→</span>
                <span className="text-green-700 font-semibold">{s.proposed.global_score}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const RescoreCategoryButton = ({ slug, label }) => {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const trigger = async () => {
    if (!window.confirm(`Recalculer uniquement la catégorie « ${label} » ?\nLes autres catégories ne seront pas touchées.`)) return;
    setBusy(true);
    setMsg("");
    try {
      await authClient.post("/admin/trust/rescore-category", { category: slug });
      setMsg("Lancé ✔");
    } catch (e) {
      setMsg(e.response?.data?.detail?.slice(0, 30) || "erreur");
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(""), 4000);
    }
  };
  return (
    <button
      onClick={trigger}
      disabled={busy}
      className="text-xs tracking-wide border border-bl-mauveDeep text-bl-mauveDeep px-3 py-1.5 hover:bg-bl-mauveDeep hover:text-white transition-colors disabled:opacity-50"
      data-testid={`rescore-${slug}`}
    >
      {busy ? "…" : msg || "Recalculer cette catégorie"}
    </button>
  );
};
